import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { 
  Calendar, Clock, Sun, Moon, Zap, Apple, Dumbbell, Brain,
  Droplets, BedDouble, CheckCircle, Bell, ChevronRight, 
  Play, Target, Heart, Sparkles, AlertCircle, Timer,
  Coffee, Utensils, Wind, Activity, Eye, MessageSquare,
  BellOff, BellRing
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';

// Pre-Game Timeline (hours before game)
const preGameTimeline = [
  {
    id: 'night_before',
    title: 'Night Before',
    timeLabel: '12+ hours before',
    icon: Moon,
    color: 'bg-indigo-500',
    pillar: 'recovery',
    tasks: [
      { id: 'sleep_early', text: 'Get to bed early (9-10 hours sleep)', icon: BedDouble, critical: true },
      { id: 'pack_bag', text: 'Pack game bag (uniform, cleats, water)', icon: Target },
      { id: 'visualize', text: 'Visualize tomorrow\'s success', icon: Brain },
      { id: 'hydrate_night', text: 'Drink water before bed', icon: Droplets },
    ]
  },
  {
    id: 'morning',
    title: 'Game Morning',
    timeLabel: '4-6 hours before',
    icon: Sun,
    color: 'bg-amber-500',
    pillar: 'nutrition',
    tasks: [
      { id: 'wake_routine', text: 'Wake up with morning stretch flow', icon: Activity },
      { id: 'breakfast', text: 'Light breakfast: Turkey wrap + fruit', icon: Coffee, critical: true },
      { id: 'hydrate_morning', text: 'Start hydrating (16-20oz water)', icon: Droplets, critical: true },
      { id: 'mental_prep', text: 'Set your Aura intention for the day', icon: Sparkles },
    ]
  },
  {
    id: 'pre_game_meal',
    title: 'Pre-Game Fuel',
    timeLabel: '3-4 hours before',
    icon: Utensils,
    color: 'bg-green-500',
    pillar: 'nutrition',
    tasks: [
      { id: 'lunch', text: 'Chicken/rice bowl with veggies', icon: Apple, critical: true },
      { id: 'avoid_heavy', text: 'Avoid heavy, greasy, or new foods', icon: AlertCircle },
      { id: 'continue_hydrate', text: 'Continue hydrating (sip, don\'t chug)', icon: Droplets },
    ]
  },
  {
    id: 'activation',
    title: 'Light Activation',
    timeLabel: '2-3 hours before',
    icon: Zap,
    color: 'bg-orange-500',
    pillar: 'training',
    tasks: [
      { id: 'dynamic_warmup', text: 'Dynamic warm-up (10 min)', icon: Wind },
      { id: 'ball_skills', text: 'Light ball skills (10 min)', icon: Target },
      { id: 'mental_focus', text: 'Mental focus & breathing exercises', icon: Brain },
    ]
  },
  {
    id: 'final_prep',
    title: 'Final Prep',
    timeLabel: '30-60 min before',
    icon: Target,
    color: 'bg-red-500',
    pillar: 'mindset',
    tasks: [
      { id: 'pre_game_snack', text: 'Banana + sports drink (electrolytes)', icon: Apple, critical: true },
      { id: 'team_warmup', text: 'Arrive for team warm-up', icon: Dumbbell },
      { id: 'game_face', text: 'Put on your game face - lock in', icon: Eye },
      { id: 'final_hydrate', text: 'Final hydration check', icon: Droplets },
    ]
  }
];

// Post-Game Timeline (hours after game)
const postGameTimeline = [
  {
    id: 'immediate',
    title: 'Immediately After',
    timeLabel: '0-15 min after',
    icon: Activity,
    color: 'bg-cyan-500',
    pillar: 'recovery',
    tasks: [
      { id: 'cool_down', text: 'Light cool down walk', icon: Wind },
      { id: 'self_check', text: 'Body scan - check for pain/tightness', icon: Heart, critical: true },
      { id: 'hydrate_post', text: 'Rehydrate immediately', icon: Droplets, critical: true },
      { id: 'chocolate_milk', text: 'Chocolate milk or recovery shake', icon: Apple, critical: true },
    ]
  },
  {
    id: 'recovery_window',
    title: 'Recovery Window',
    timeLabel: '15-60 min after',
    icon: Heart,
    color: 'bg-pink-500',
    pillar: 'recovery',
    tasks: [
      { id: 'stretch', text: 'Full body stretch (10-15 min)', icon: Activity },
      { id: 'foam_roll', text: 'Foam roll tight areas', icon: Moon },
      { id: 'ice_sore', text: 'Ice any sore joints (15 min)', icon: Droplets },
      { id: 'recovery_smoothie', text: 'Recovery smoothie (protein + berries + spinach)', icon: Apple },
    ]
  },
  {
    id: 'evening_recovery',
    title: 'Evening Recovery',
    timeLabel: '2-4 hours after',
    icon: Moon,
    color: 'bg-indigo-500',
    pillar: 'recovery',
    tasks: [
      { id: 'epsom_bath', text: 'Epsom salt bath (15-20 min)', icon: Droplets },
      { id: 'compression', text: 'Wear compression if needed', icon: Activity },
      { id: 'balanced_dinner', text: 'Balanced dinner with protein & carbs', icon: Utensils },
      { id: 'massage', text: 'Light massage or massage gun', icon: Heart },
    ]
  },
  {
    id: 'wind_down',
    title: 'Wind Down',
    timeLabel: 'Before bed',
    icon: BedDouble,
    color: 'bg-purple-500',
    pillar: 'recovery',
    tasks: [
      { id: 'parent_checkin', text: 'Parent/coach check-in on how you feel', icon: MessageSquare },
      { id: 'log_recovery', text: 'Log recovery in Aura app', icon: CheckCircle },
      { id: 'film_review', text: 'Optional: Quick film review highlights', icon: Eye },
      { id: 'early_bed', text: 'Get to bed early for full recovery', icon: BedDouble, critical: true },
    ]
  }
];

// Alert times for game day
const defaultAlerts = [
  { id: 'night_before', label: 'Night before reminder', time: '20:00', enabled: true },
  { id: 'morning_wake', label: 'Game day wake up', time: '07:00', enabled: true },
  { id: 'breakfast', label: 'Breakfast reminder', time: '08:00', enabled: true },
  { id: 'pre_game_meal', label: 'Pre-game meal', time: '11:30', enabled: true },
  { id: 'activation', label: 'Light activation', time: '13:00', enabled: true },
  { id: 'leave_home', label: 'Leave for game', time: '14:30', enabled: true },
  { id: 'post_game', label: 'Post-game recovery', time: '18:00', enabled: false },
];

const GameDayRoutinePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('pre'); // 'pre' or 'post'
  const [gameDate, setGameDate] = useState(new Date().toISOString().split('T')[0]);
  const [gameTime, setGameTime] = useState('15:30'); // Default 3:30 PM
  const [completedTasks, setCompletedTasks] = useState({});
  const [alerts, setAlerts] = useState(defaultAlerts);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [scheduledServerAlerts, setScheduledServerAlerts] = useState([]);
  
  // NEW: Calendar Integration State
  const [nextGame, setNextGame] = useState(null);
  const [tournamentContext, setTournamentContext] = useState(null);
  const [loadingNextGame, setLoadingNextGame] = useState(true);
  const [hoursUntilGame, setHoursUntilGame] = useState(null);
  const [dayType, setDayType] = useState('upcoming');
  
  const {
    isSupported: pushSupported,
    permission: pushPermission,
    isSubscribed,
    subscribe: subscribePush,
    scheduleGameDayAlerts,
    getScheduledAlerts,
    cancelAlerts
  } = usePushNotifications();

  // NEW: Fetch next game from Calendar
  useEffect(() => {
    const fetchNextGame = async () => {
      try {
        const token = localStorage.getItem('aura_token');
        const response = await axios.get(`${API}/calendar-hub/next-game`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.has_upcoming_game) {
          setNextGame(response.data.game);
          setTournamentContext(response.data.tournament_context);
          setHoursUntilGame(response.data.hours_until_game);
          setDayType(response.data.day_type);
          
          // Auto-set game date and time from calendar
          if (response.data.game?.start_time) {
            const gameDt = response.data.game.start_time;
            if (gameDt.includes('T')) {
              setGameDate(gameDt.split('T')[0]);
              const timePart = gameDt.split('T')[1];
              if (timePart) {
                setGameTime(timePart.substring(0, 5)); // HH:MM
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch next game:', error);
      } finally {
        setLoadingNextGame(false);
      }
    };
    
    fetchNextGame();
  }, []);

  // Load saved progress and fetch server alerts
  useEffect(() => {
    const saved = localStorage.getItem('aura_gameday_progress');
    if (saved) {
      setCompletedTasks(JSON.parse(saved));
    }
    const savedAlerts = localStorage.getItem('aura_gameday_alerts');
    if (savedAlerts) {
      setAlerts(JSON.parse(savedAlerts));
    }
    
    // Fetch scheduled alerts from server
    fetchScheduledAlerts();
  }, []);

  const fetchScheduledAlerts = async () => {
    const result = await getScheduledAlerts();
    if (result.success) {
      setScheduledServerAlerts(result.alerts);
    }
  };

  // Calculate alert times based on game time
  const calculateAlertTime = (alertTime, gameTimeStr) => {
    const [gameHour, gameMinute] = gameTimeStr.split(':').map(Number);
    const today = new Date();
    const gameDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), gameHour, gameMinute);
    
    // Map alert IDs to hours before game
    const hoursBeforeGame = {
      'night_before': 18, // Night before (for next day game)
      'morning_wake': 8,
      'breakfast': 7,
      'pre_game_meal': 4,
      'activation': 2.5,
      'leave_home': 1,
      'post_game': -3, // 3 hours after
    };
    
    const hoursBefore = hoursBeforeGame[alertTime] || 0;
    const alertDate = new Date(gameDate.getTime() - (hoursBefore * 60 * 60 * 1000));
    return alertDate;
  };

  // Schedule server-side push notifications
  const scheduleAlerts = async () => {
    // First ensure we have push permission and subscription
    if (!isSubscribed) {
      const subResult = await subscribePush();
      if (!subResult.success) {
        toast.error('Please enable notifications first');
        return;
      }
    }

    // Prepare alerts for server
    const alertsToSchedule = alerts
      .filter(a => a.enabled)
      .map(alert => {
        // Map alert IDs to hours before game
        const hoursBeforeGame = {
          'night_before': 18,
          'morning_wake': 8,
          'breakfast': 7,
          'pre_game_meal': 4,
          'activation': 2.5,
          'leave_home': 1,
          'post_game': -3,
        };
        
        return {
          type: alert.id,
          message: alert.label,
          hours_before: hoursBeforeGame[alert.id] || 0,
          enabled: true
        };
      });

    const result = await scheduleGameDayAlerts(gameDate, gameTime, alertsToSchedule);
    
    if (result.success) {
      toast.success(`${result.data.alerts.length} alerts scheduled!`);
      setShowAlertSettings(false);
      fetchScheduledAlerts();
    } else {
      toast.error('Failed to schedule alerts');
    }
  };

  const handleCancelAlerts = async () => {
    const result = await cancelAlerts(gameDate);
    if (result.success) {
      toast.success('Alerts cancelled');
      fetchScheduledAlerts();
    }
  };

  const toggleTask = (phaseId, taskId) => {
    const key = `${phaseId}_${taskId}`;
    const updated = { ...completedTasks, [key]: !completedTasks[key] };
    setCompletedTasks(updated);
    localStorage.setItem('aura_gameday_progress', JSON.stringify(updated));
    
    if (!completedTasks[key]) {
      toast.success('Task completed! Keep it up!');
    }
  };

  const toggleAlert = (alertId) => {
    const updated = alerts.map(a => 
      a.id === alertId ? { ...a, enabled: !a.enabled } : a
    );
    setAlerts(updated);
    localStorage.setItem('aura_gameday_alerts', JSON.stringify(updated));
  };

  const resetProgress = () => {
    setCompletedTasks({});
    localStorage.removeItem('aura_gameday_progress');
    toast.success('Progress reset for new game day!');
  };

  const timeline = activeView === 'pre' ? preGameTimeline : postGameTimeline;
  
  const calculateProgress = () => {
    const totalTasks = timeline.reduce((sum, phase) => sum + phase.tasks.length, 0);
    const completed = timeline.reduce((sum, phase) => {
      return sum + phase.tasks.filter(task => completedTasks[`${phase.id}_${task.id}`]).length;
    }, 0);
    return totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;
  };

  const getPillarColor = (pillar) => {
    const colors = {
      training: 'text-orange-400',
      nutrition: 'text-green-400',
      recovery: 'text-cyan-400',
      mindset: 'text-purple-400',
    };
    return colors[pillar] || 'text-white';
  };

  return (
    <DashboardLayout title="Game Day">
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
            Game Day Routine
          </h1>
          <p className="text-aura-text-muted mt-1">Your complete pre & post game playbook</p>
        </div>

        {/* NEW: Next Game Card - Calendar Connected */}
        {loadingNextGame ? (
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-aura-primary mx-auto"></div>
              <p className="text-aura-text-muted mt-2 text-sm">Loading from calendar...</p>
            </CardContent>
          </Card>
        ) : nextGame ? (
          <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center">
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-heading font-bold text-white uppercase tracking-tight">
                        {nextGame.opponent ? `vs ${nextGame.opponent}` : nextGame.title}
                      </p>
                      {dayType === 'game_day' && (
                        <Badge className="bg-red-500 text-white animate-pulse">TODAY</Badge>
                      )}
                      {dayType === 'game_imminent' && (
                        <Badge className="bg-red-500 text-white animate-pulse">GAME SOON</Badge>
                      )}
                    </div>
                    <p className="text-sm text-green-300">
                      {new Date(nextGame.start_time).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      {' at '}
                      {new Date(nextGame.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                    {nextGame.location && (
                      <p className="text-xs text-aura-text-muted">{nextGame.location}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {hoursUntilGame !== null && (
                    <div>
                      <p className="font-heading font-bold text-2xl text-green-400">
                        {hoursUntilGame < 1 ? `${Math.round(hoursUntilGame * 60)}m` : 
                         hoursUntilGame < 24 ? `${Math.round(hoursUntilGame)}h` : 
                         `${Math.round(hoursUntilGame / 24)}d`}
                      </p>
                      <p className="text-xs text-aura-text-muted">until game</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Tournament Context */}
              {tournamentContext && (
                <div className="mt-3 pt-3 border-t border-green-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-green-500/50 text-green-400">
                        {tournamentContext.tournament_name}
                      </Badge>
                      <span className="text-xs text-aura-text-muted">
                        Game {tournamentContext.game_number} of {tournamentContext.total_games}
                      </span>
                    </div>
                    {tournamentContext.is_multi_day && (
                      <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 text-xs">
                        Multi-Day Tournament
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* No upcoming game - show manual selector */
          <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/20 border-aura-primary/30">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-aura-primary rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white uppercase tracking-tight">No Upcoming Games</p>
                    <p className="text-sm text-aura-text-muted">Add games in Calendar Hub to see them here</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/calendar-hub')}
                  className="bg-aura-primary"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Go to Calendar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Game Time Selector (for manual override) */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-aura-text-muted" />
                <div>
                  <p className="text-sm font-medium text-white">Game Time</p>
                  <p className="text-xs text-aura-text-muted">
                    {nextGame ? 'Auto-set from calendar' : 'Set manually for checklist timing'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Select value={gameTime} onValueChange={setGameTime}>
                  <SelectTrigger className="w-32 bg-aura-bg border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '15:30', '16:00', '17:00', '18:00', '19:00'].map(time => (
                      <SelectItem key={time} value={time} className="text-white">
                        {parseInt(time) > 12 ? `${parseInt(time) - 12}:${time.split(':')[1]} PM` : `${time} AM`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAlertSettings(true)}
                  className="border-white/10 text-white"
                >
                  <Bell className="w-4 h-4 mr-1" />
                  Alerts
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pre/Post Toggle */}
        <div className="flex bg-aura-surface rounded-xl p-1 border border-white/10">
          <button
            onClick={() => setActiveView('pre')}
            className={`flex-1 py-3 px-4 rounded-lg font-heading uppercase tracking-wider text-sm transition-colors ${
              activeView === 'pre' 
                ? 'bg-aura-primary text-white' 
                : 'text-aura-text-muted hover:text-white'
            }`}
          >
            <Sun className="w-4 h-4 inline mr-2" />
            Pre-Game
          </button>
          <button
            onClick={() => setActiveView('post')}
            className={`flex-1 py-3 px-4 rounded-lg font-heading uppercase tracking-wider text-sm transition-colors ${
              activeView === 'post' 
                ? 'bg-aura-secondary text-white' 
                : 'text-aura-text-muted hover:text-white'
            }`}
          >
            <Moon className="w-4 h-4 inline mr-2" />
            Post-Game
          </button>
        </div>

        {/* Progress Bar */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-aura-text-muted">
                {activeView === 'pre' ? 'Pre-Game' : 'Post-Game'} Progress
              </span>
              <span className="text-sm font-bold text-aura-primary">{calculateProgress()}%</span>
            </div>
            <Progress value={calculateProgress()} className="h-2" />
            <div className="flex justify-between mt-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetProgress}
                className="text-aura-text-muted text-xs"
              >
                Reset for new game
              </Button>
              <div className="flex items-center gap-2 text-xs text-aura-text-muted">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span> Training
                <span className="w-2 h-2 bg-green-400 rounded-full"></span> Nutrition
                <span className="w-2 h-2 bg-cyan-400 rounded-full"></span> Recovery
                <span className="w-2 h-2 bg-purple-400 rounded-full"></span> Mindset
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <div className="space-y-4">
          {timeline.map((phase, phaseIdx) => {
            const Icon = phase.icon;
            const phaseCompleted = phase.tasks.every(task => completedTasks[`${phase.id}_${task.id}`]);
            const phaseProgress = phase.tasks.filter(task => completedTasks[`${phase.id}_${task.id}`]).length;
            
            return (
              <Card 
                key={phase.id} 
                className={`bg-aura-surface border-white/10 ${phaseCompleted ? 'ring-2 ring-green-500/30' : ''}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${phase.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="font-heading font-bold text-white uppercase tracking-tight text-base flex items-center gap-2">
                          {phase.title}
                          {phaseCompleted && <CheckCircle className="w-4 h-4 text-green-400" />}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-aura-text-muted" />
                          <span className="text-xs text-aura-text-muted">{phase.timeLabel}</span>
                          <Badge className={`${getPillarColor(phase.pillar)} bg-white/5 border-0 text-xs capitalize`}>
                            {phase.pillar}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-aura-text-muted">
                      {phaseProgress}/{phase.tasks.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {phase.tasks.map((task) => {
                    const TaskIcon = task.icon;
                    const isCompleted = completedTasks[`${phase.id}_${task.id}`];
                    
                    return (
                      <button
                        key={task.id}
                        onClick={() => toggleTask(phase.id, task.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                          isCompleted 
                            ? 'bg-green-500/10 border border-green-500/30' 
                            : 'bg-white/5 border border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isCompleted ? 'bg-green-500' : 'border-2 border-white/30'
                        }`}>
                          {isCompleted && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                        <TaskIcon className={`w-4 h-4 flex-shrink-0 ${isCompleted ? 'text-green-400' : 'text-aura-text-muted'}`} />
                        <span className={`flex-1 text-sm ${isCompleted ? 'text-green-300 line-through' : 'text-white'}`}>
                          {task.text}
                        </span>
                        {task.critical && !isCompleted && (
                          <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">Key</Badge>
                        )}
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Tips */}
        <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-heading font-bold text-white uppercase tracking-tight">Mindset Tip</h3>
                <p className="text-sm text-aura-text-muted mt-1">
                  {activeView === 'pre' 
                    ? '"Control what you can control. Your preparation, effort, and attitude are in your hands. Trust your training and play your game."'
                    : '"Win or learn, never lose. Review what went well and what to improve. Recovery is where champions are made."'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nutrition Summary */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-white uppercase tracking-wider text-base flex items-center gap-2">
              <Apple className="w-5 h-5 text-green-400" />
              {activeView === 'pre' ? 'Pre-Game Nutrition' : 'Post-Game Nutrition'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeView === 'pre' ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Coffee className="w-4 h-4 text-amber-400" />
                    <span className="text-white text-sm">Breakfast</span>
                  </div>
                  <span className="text-aura-text-muted text-sm">Turkey wrap + fruit</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Utensils className="w-4 h-4 text-green-400" />
                    <span className="text-white text-sm">Pre-Game Meal (3-4hr)</span>
                  </div>
                  <span className="text-aura-text-muted text-sm">Chicken + rice + veggies</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-orange-400" />
                    <span className="text-white text-sm">Pre-Game Snack (30min)</span>
                  </div>
                  <span className="text-aura-text-muted text-sm">Banana + electrolytes</span>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-cyan-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Droplets className="w-4 h-4 text-cyan-400" />
                    <span className="text-white text-sm">Immediate (0-15min)</span>
                  </div>
                  <span className="text-aura-text-muted text-sm">Chocolate milk + water</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-pink-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-400" />
                    <span className="text-white text-sm">Recovery (30-60min)</span>
                  </div>
                  <span className="text-aura-text-muted text-sm">Protein smoothie + berries</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-indigo-500/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-indigo-400" />
                    <span className="text-white text-sm">Dinner</span>
                  </div>
                  <span className="text-aura-text-muted text-sm">Balanced meal + hydration</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alert Settings Dialog */}
      <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
        <DialogContent className="bg-aura-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Bell className="w-5 h-5 text-aura-primary" />
              Game Day Alerts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            {/* Push Notification Status */}
            {!pushSupported && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <BellOff className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm font-medium">Push notifications not supported</span>
                </div>
                <p className="text-xs text-aura-text-muted mt-1">
                  Your browser doesn't support push notifications.
                </p>
              </div>
            )}
            
            {pushSupported && !isSubscribed && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">Enable Push Notifications</span>
                </div>
                <p className="text-xs text-aura-text-muted mb-2">
                  Get reminders even when the app is closed.
                </p>
                <Button 
                  size="sm"
                  onClick={subscribePush}
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
                >
                  <BellRing className="w-3 h-3 mr-1" />
                  Enable Notifications
                </Button>
              </div>
            )}
            
            {pushSupported && isSubscribed && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">Push notifications enabled</span>
              </div>
            )}

            {/* Game Date Selection */}
            <div className="space-y-2">
              <label className="text-sm text-aura-text-muted">Game Date</label>
              <Input
                type="date"
                value={gameDate}
                onChange={(e) => setGameDate(e.target.value)}
                className="bg-aura-bg border-white/10 text-white"
              />
            </div>

            {/* Scheduled Alerts Count */}
            {scheduledServerAlerts.length > 0 && (
              <div className="p-3 bg-aura-primary/10 border border-aura-primary/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Timer className="w-4 h-4 text-aura-primary" />
                    <span className="text-sm text-white">
                      {scheduledServerAlerts.length} alerts scheduled
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelAlerts}
                    className="text-red-400 text-xs hover:text-red-300"
                  >
                    Cancel All
                  </Button>
                </div>
              </div>
            )}

            <p className="text-sm text-aura-text-muted">
              Select which reminders you want for your {gameTime.includes('PM') || parseInt(gameTime) >= 12 ? '' : 'AM'} game.
            </p>
            {alerts.map(alert => (
              <div 
                key={alert.id}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
              >
                <div>
                  <p className="text-white text-sm">{alert.label}</p>
                  <p className="text-xs text-aura-text-muted">
                    {(() => {
                      const alertTime = calculateAlertTime(alert.id, gameTime);
                      return alertTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    })()}
                  </p>
                </div>
                <Switch
                  checked={alert.enabled}
                  onCheckedChange={() => toggleAlert(alert.id)}
                />
              </div>
            ))}
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={scheduleAlerts}
                className="flex-1 bg-aura-primary hover:bg-aura-primary-hover text-white"
                disabled={!pushSupported}
              >
                <Timer className="w-4 h-4 mr-2" />
                Schedule Alerts
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowAlertSettings(false)}
                className="border-white/20 text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default GameDayRoutinePage;
